import { sharePointDriveService } from '@/services/sharepoint-drive/sharepoint-drive-service';
import type { ItemReference } from '@/services/sharepoint-drive/sharepoint-drive-service';
import { directoryKeys } from '@/utils/queryKeys';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { getParentPath } from '../utils';
import type { FileManagerMountsState } from '../logic/core/useFileManagerMounts';
import type { FileSystemItem } from '../types';

export type CopyItemVariables = {
  item: FileSystemItem;
  destinationParentPath: string;
  /**
   * Pre-resolved destination reference, e.g. from a materialized drop-target item's own id/driveId.
   * When present, skips the getItemReference() round-trip.
   */
  destinationParent?: ItemReference;
  /**
   * Name to give the copy. Required in practice for an in-place duplicate (same folder, so the
   * source's own name would collide under conflictBehavior 'fail'); omitted by the cross-mount drop
   * path, which keeps the source's name.
   */
  name?: string;
  /** Abandons the monitor poll (never the copy) when the file manager unmounts. */
  signal?: AbortSignal;
};

/**
 * Copies an item into another folder — the verb a drop across the mount boundary resolves to, since
 * Graph's move cannot leave its drive.
 *
 * Sibling to useMoveItemMutation, and takes `mounts` explicitly for the same reason: both are
 * constructed from provideFileManagerTransfer(), called directly in FileManager.vue's own setup(),
 * which cannot inject its own provide() call.
 *
 * Reports nothing itself — neither a toast on failure nor one on success. The caller (runTransfer)
 * copies a whole selection at once and aggregates one message for the batch, exactly as
 * useFileManagerDelete does; a per-item toast here would mean twenty notifications for a twenty-item
 * drop, and would double up with that summary.
 */
export const useCopyItemMutation = (
  mounts: Pick<FileManagerMountsState, 'resolve'>,
) => {
  const queryClient = useQueryClient();
  const { resolve } = mounts;

  return useMutation({
    mutationKey: ['directoryService', 'copyItem'],
    mutationFn: async ({
      item,
      destinationParentPath,
      destinationParent,
      name,
      signal,
    }: CopyItemVariables) => {
      let destParent = destinationParent;
      if (!destParent) {
        const target = resolve(destinationParentPath);
        if (!target) {
          throw new Error(
            `Cannot copy into ${destinationParentPath}: no accessible library`,
          );
        }
        destParent = await sharePointDriveService.getItemReference(target);
      }
      // The third argument stays the VIRTUAL destination path — it is what the returned item's path
      // is built from, and everything above this mutation navigates by virtual paths.
      return sharePointDriveService.copyItem(
        item,
        destParent,
        destinationParentPath,
        { name, signal },
      );
    },
    onSuccess: (_copiedItem, { item, destinationParentPath }) => {
      // Only the destination is touched — unlike a move, the source folder is unchanged, so there is
      // nothing to invalidate there.
      queryClient.invalidateQueries({
        queryKey: directoryKeys.children(destinationParentPath),
      });

      // A copied directory can flip the destination's own `hasFolderChildren` (its first subfolder),
      // and that flag lives on the destination's entry in ITS parent's children list.
      if (item.type === 'directory') {
        queryClient.invalidateQueries({
          queryKey: directoryKeys.children(getParentPath(destinationParentPath)),
        });
      }
    },
  });
};
